import { Alert, AlertDescription, Button } from '@osteopro/ui';
import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '../../hooks/api/use-plans.js';
import {
  goToStripe,
  useSubscription,
  useSubscriptionPortal,
} from '../../hooks/api/use-subscription.js';

/**
 * Bandeau d'impayé. **Non bloquant** : il informe et donne le seul chemin utile — le portail, où
 * le moyen de paiement se corrige.
 *
 * Un cabinet en impayé n'est pas un cabinet suspendu, et les deux écrans ne se confondent pas :
 * celui-ci laisse travailler, l'autre barre tout. Couper l'accès aux dossiers de santé d'un
 * praticien parce qu'une carte a expiré serait hors de proportion.
 *
 * La **condition** se lit dans les droits, que la barre latérale charge déjà : ce bandeau est
 * rendu sur tous les écrans, et une requête de plus par page pour une situation rare serait mal
 * placée. L'abonnement lui-même n'est lu que lorsque l'impayé est avéré, pour savoir si le
 * portail s'ouvre — proposer un bouton qui répondrait « ce cabinet n'a jamais payé » serait une
 * promesse en l'air.
 */
export function PastDueBanner() {
  const { t } = useTranslation();
  const entitlements = useEntitlements();
  const pastDue = entitlements.data?.status === 'past_due';
  const subscription = useSubscription(pastDue);
  const portal = useSubscriptionPortal();

  if (!pastDue) return null;

  const manage = async () => {
    const { url } = await portal.mutateAsync();
    goToStripe(url);
  };

  return (
    <Alert
      variant="destructive"
      className="rounded-none border-x-0 border-t-0 bg-destructive/5 px-4 py-2.5 md:px-6 [&_span]:[overflow-wrap:anywhere]"
      data-testid="banner-past-due"
    >
      <CreditCard className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1">{t('plans:past_due.message')}</span>
        {subscription.data?.portalAvailable === true && (
          <Button
            size="sm"
            variant="outline"
            disabled={portal.isPending}
            onClick={() => void manage()}
            data-testid="button-billing-portal"
          >
            {t('plans:past_due.manage')}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
